import React, {useCallback, useEffect, useMemo, useState} from "react"
import ClientSelect from "../../components/form-v2/searchable-select/ClientSelect";
import {Client, Department} from "../../types/userManagement";
import {useGetCheckInConfigurations} from "./useGetCheckInConfigurations";
import {IconButton, ToggleButton} from "../../features/management-v2/import/inputs";
import AddIcon from "@mui/icons-material/Add";
import {AutoCheckInConfig, CountryRule, RiskRule} from "./types";
import {useGetHeader} from "./hooks";
import {useCreateDefaultCheckInConfig} from "./useCreateDefaultCheckInConfig";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import BasicText from "../../components/form-v2/basic-text";
import {useCreateCheckInConfig} from "./useCreateCheckInConfig";
import MultiInputSingleField from "../../components/form-v2/multi-input/MultiInputSingleField";
import {DepartmentSelectV2} from "../../lib/departments/DepartmentSelectV2";
import {ButtonV2} from "../../components/ui/ButtonsV2/ButtonV2";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import BasicSelect from "../../components/form-v2/basic-select";
import clsx from "clsx";
import {RiskRow} from "./RiskRow";
import {CountryRow} from "./CountryRow";
import {CountrySelectSingle} from "../../components/form-v2/searchable-select/CountrySelectSingle";
import {useUpdateCheckInConfig} from "./useUpdateCheckInConfig";
import {useDepartmentApi} from "../../api/platform/useDepartments";
import {defaultRiskRules} from "./defaults";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import {DeleteProfileModal} from "./DeleteProfileModal";
import {countries} from "../../lib/countries";

export function CheckInConfiguration() {
    const [client, setClient] = useState<undefined | Client>()
    const [selectedProfile, setSelectedProfile] = useState<undefined | AutoCheckInConfig>()

    const {
        data: profiles,
        isLoading: profilesLoading,
        refetch: refetchProfiles
    } = useGetCheckInConfigurations(client?.id)


    const {
        submit: createDefault,
        submitting: creatingDefault,
        error: createDefaultError
    } = useCreateDefaultCheckInConfig(client, refetchProfiles)

    const {
        submit: create,
        submitting: creating,
        error: createError,
        name,
        setName
    } = useCreateCheckInConfig(client, refetchProfiles)


    useEffect(() => {
        setSelectedProfile(undefined)
    }, [client]);

    useEffect(() => {
        if(!selectedProfile) return
        if(!profiles) return
        if(!profiles.find(p => p.id == selectedProfile.id)){
            setSelectedProfile(undefined)
        }

    }, [profiles, selectedProfile]);

    const otherProfiles = useMemo(() => {
        if (!profiles) return [];
        if (!selectedProfile) return [];
        return profiles.filter(p => p.id != selectedProfile.id)
    }, [selectedProfile, profiles])


    return (
        <div className="h-full pt-36 pb-16 px-7">
            <div className="mt-5 px-6 pt-7 pb-8 relative flex flex-row gap-8">
                <div className="bg-white rounded-md shadow-lg p-8 w-[400px] flex flex-col gap-8">
                    <div>
                        <h2 className="font-semibold text-lg mb-4">Select Client</h2>
                        <ClientSelect clients={client ? [client] : []} onSelectionChange={(c) => setClient(c[0])}/>
                    </div>
                    {!profilesLoading && profiles != undefined && (
                        <div className="flex flex-col gap-4">
                            <h2 className="font-semibold text-lg">Profiles ({profiles.length})</h2>
                            {profiles.length == 0 && (
                                <>
                                    <IconButton text="Create Default Profile" icon={<AddIcon/>}
                                                disabled={creatingDefault}
                                                onClick={createDefault}/>
                                    <ErrorMessage text={createDefaultError}/>

                                </>
                            )}

                            {profiles.map(profile => (
                                <IconButton
                                    text={profile.name || "Unnamed"}
                                    key={profile.id}
                                    onClick={() => {
                                        setSelectedProfile(profile)
                                    }}
                                    color={selectedProfile?.id == profile.id ? "blue" : "grey"}
                                    icon={selectedProfile?.id == profile.id ? <RadioButtonCheckedIcon/> :
                                        <RadioButtonUncheckedIcon/>}
                                />
                            ))}


                            {profiles.length != 0 && (
                                <>
                                    <h2 className="font-semibold text-lg">Create New Profile</h2>

                                    <BasicText value={name} onChange={setName} placeholder={"Profile Name"}/>
                                    <IconButton text="Create Profile" icon={<AddIcon/>} disabled={creating}
                                                onClick={create}/>
                                    <ErrorMessage text={createError}/>

                                </>
                            )}

                        </div>
                    )}


                </div>

                <ProfileEdit profile={selectedProfile} client={client} key={selectedProfile?.id}
                             refetchProfiles={refetchProfiles} otherProfiles={otherProfiles}/>

            </div>
        </div>
    )
}

type ProfileEditProps = {
    profile: AutoCheckInConfig | undefined
    client: Client | undefined
    refetchProfiles: () => void
    otherProfiles: AutoCheckInConfig[]
}

function ProfileEdit({profile, client, refetchProfiles, otherProfiles}: ProfileEditProps) {

    const [mode, setMode] = useState(profile?.mode || "risk")
    const [selectedCountryISO, setSelectedCountryISO] = useState<string>()
    const [riskRules, setRiskRules] = useState<RiskRule[]>(profile?.riskRules?.length ? profile.riskRules : defaultRiskRules)
    const [countryRules, setCountryRules] = useState<CountryRule[]>(profile?.countryRules?.length ? profile.countryRules : [])
    const [departmentAddError, setDepartmentAddError] = useState<string>()

    const [showDelete, setShowDelete] = useState<boolean>(false)

    const {
        submit: submitUpdate,
        submitting: submittingUpdate,
        error: updateError,
        message: updateMessage,
    } = useUpdateCheckInConfig(profile?.id, refetchProfiles)


    const header = useGetHeader(client, profile)
    const deptApi = useDepartmentApi()
    const [departments, setDepartments] = useState<Department[]>([])

    useEffect(() => {
        if (!profile) return
        const fetchDepartments = async () => {
            const results = await Promise.all(profile.departmentIDs.map(id => deptApi.get(id).then(([d]) => d)))
            setDepartments(results.filter((d): d is Department => d != null))
        }
        fetchDepartments()
    }, [profile])

    useEffect(() => {
        if (mode == "risk" && riskRules.length == 0) {
            setRiskRules(defaultRiskRules)
        }

    }, [mode, riskRules]);

    const [selectedDepartment, setSelectedDepartment] = useState<Department | undefined>(undefined)

    const addDepartment = useCallback(() => {
        if (!selectedDepartment) {
            setDepartmentAddError("no department selected")
            return
        }

        if (departments.find(d => d.id == selectedDepartment.id)) {
            setDepartmentAddError("department already selected")
            return
        }

        for (const op of otherProfiles) {
            if (op.departmentIDs.includes(selectedDepartment.id)) {
                setDepartmentAddError(`department already selected in profile (${op.name})`)
                return
            }
        }

        const newDepartments = [...departments]
        newDepartments.push(selectedDepartment)
        setDepartments(newDepartments)
        setDepartmentAddError(undefined)
    }, [selectedDepartment, departments])

    const removeDepartment = useCallback((index: number) => {
        const newDepartments = [...departments]
        newDepartments.splice(index, 1)
        setDepartments(newDepartments)
    }, [departments])


    const [excludedCountries, setExcludedCountries] = useState(profile?.excludeCountries ?? [])
    const [excludeUsersCountry, setExcludeUsersCountry] = useState(profile?.excludeUserCountry ?? false)


    const addItem = useCallback(() => {


        if (mode === "risk") {
            const newItem = {
                minRisk: riskRules[riskRules.length - 1].maxRisk + 1,
                maxRisk: riskRules[riskRules.length - 1].maxRisk + 1,
                times: [...riskRules[riskRules.length - 1].times],
                onArrivalTravel: riskRules[riskRules.length - 1].onArrivalTravel,
                onArrivalAccommodation: riskRules[riskRules.length - 1].onArrivalAccommodation,
                onDepartureTravel: riskRules[riskRules.length - 1].onDepartureTravel,
                onDepartureAccommodation: riskRules[riskRules.length - 1].onDepartureAccommodation,
                timeGracePeriod: riskRules[riskRules.length - 1].timeGracePeriod,
                arrivalGracePeriod: riskRules[riskRules.length - 1].arrivalGracePeriod,
                departureGracePeriod: riskRules[riskRules.length - 1].departureGracePeriod,
            }
            setRiskRules([...riskRules, newItem])
        }

        if (mode == "country") {
            console.log(
                !selectedCountryISO,
                countryRules
            )
            if (!selectedCountryISO) return
            if (countryRules.find(r => r.countryISO == selectedCountryISO)) return

            const newItem = (() => {
                if (countryRules.length != 0) {
                    return {
                        countryISO: selectedCountryISO,
                        times: [...countryRules[countryRules.length - 1].times],
                        onArrivalTravel: countryRules[countryRules.length - 1].onArrivalTravel,
                        onArrivalAccommodation: countryRules[countryRules.length - 1].onArrivalAccommodation,
                        onDepartureTravel: countryRules[countryRules.length - 1].onDepartureTravel,
                        onDepartureAccommodation: countryRules[countryRules.length - 1].onDepartureAccommodation,
                        timeGracePeriod: countryRules[countryRules.length - 1].timeGracePeriod,
                        arrivalGracePeriod: countryRules[countryRules.length - 1].arrivalGracePeriod,
                        departureGracePeriod: countryRules[countryRules.length - 1].departureGracePeriod,
                    }
                }

                return {
                    countryISO: selectedCountryISO,
                    times: [],
                    onArrivalTravel: false,
                    onArrivalAccommodation: false,
                    onDepartureTravel: false,
                    onDepartureAccommodation: false,
                    timeGracePeriod: 60,
                    arrivalGracePeriod: 60,
                    departureGracePeriod: 60,
                }

            })()

            setCountryRules([...countryRules, newItem])

            console.log([...countryRules, newItem])

            setSelectedCountryISO(undefined)
        }

    }, [riskRules, countryRules, selectedCountryISO, mode])

    const canAdd = useMemo(() => {
        if (mode == "country") return true
        if (riskRules.length == 0) return true
        const last = riskRules[riskRules.length - 1]
        return last.maxRisk < 9
    }, [mode, riskRules])

    const removeRisk = useCallback((index: number) => {
        const newRows = [...riskRules]
        newRows.splice(index, 1)
        setRiskRules(newRows)
    }, [riskRules])

    const updateRisk = useCallback((index: number, row: RiskRule) => {
        const newRows = [...riskRules]
        newRows[index] = row
        setRiskRules(newRows)
    }, [riskRules])


    const removeCountry = useCallback((index: number) => {
        const newRows = [...countryRules]
        newRows.splice(index, 1)
        setCountryRules(newRows)
    }, [countryRules])

    const updateCountry = useCallback((index: number, row: CountryRule) => {
        const newRows = [...countryRules]
        newRows[index] = row
        setCountryRules(newRows)
    }, [countryRules])

    if (!profile || !client) {
        return <div className="bg-white rounded-md shadow-lg p-8 flex-grow flex flex-col gap-4">
            <h2 className="font-semibold text-lg">{header}</h2>
        </div>
    }

    const modeOptions = useMemo(() => {
        return [
            {label: "Risk", value: "risk",},
            {label: "Country", value: "country"},
        ]
    }, [])

    const handleSetMode = useCallback((val: string | undefined) => {
        if (val == "risk" || val == "country") {
            setMode(val)
        }
    }, [])


    const handleSave = useCallback(() => {

        const countryRulesSorted = countryRules.sort((a, b) => {
            const countryA = countries.find(c => c.countryCode == a.countryISO)
            const countryB = countries.find(c => c.countryCode == b.countryISO)

            if(!countryA) return 0
            if(!countryB) return 0

            return countryA.countryNameEn.toLowerCase().localeCompare(countryB.countryNameEn.toLowerCase())

        })

        const excludedCountriesSorted = excludedCountries.sort((a, b) => {
            const countryA = countries.find(c => c.countryCode == a)
            const countryB = countries.find(c => c.countryCode == b)
            if(!countryA) return 0
            if(!countryB) return 0

            return countryA.countryNameEn.toLowerCase().localeCompare(countryB.countryNameEn.toLowerCase())
        })

        submitUpdate({
            countryRules: mode == "country" ? countryRulesSorted : [],
            departmentIDs: departments.map(d => d.id),
            excludeUsersCountry: excludeUsersCountry,
            excludedCountries: mode == "risk" ? excludedCountriesSorted : [],
            mode: mode,
            riskRules: mode == "risk" ? riskRules : [],
        })


    }, [mode, countryRules, departments, excludeUsersCountry, excludedCountries, riskRules])

    return (
        <>
            <DeleteProfileModal profile={profile} show={showDelete} setShow={setShowDelete} refetch={refetchProfiles}/>

            <div className="bg-white rounded-md shadow-lg p-8 flex-grow flex flex-col gap-4">
                <div className="flex items-center">
                    <h2 className="font-semibold text-lg flex-1">{header}</h2>
                    <div className="w-[200px]">
                        <IconButton color="red" text="Delete Profile" icon={<DeleteIcon/>} disabled={profile.default} onClick={() => setShowDelete(true)} />
                    </div>
                </div>
                <BasicSelect label={"Select Mode"} options={modeOptions} value={mode} onChange={handleSetMode}/>
                <Heading mode={mode}/>
                {mode == "risk" && riskRules.map((row, index) => (

                    <React.Fragment key={index}>
                        <RiskRow
                            row={row}
                            index={index}
                            prev={riskRules[index - 1] ?? undefined}
                            next={riskRules[index + 1] ?? undefined}
                            remove={removeRisk}
                            update={updateRisk}
                        />
                        {index < riskRules.length - 1 && <hr className="opacity-20"/>}
                    </React.Fragment>
                ))}

                {mode == "country" && countryRules.map((row, index) => (

                    <React.Fragment key={index}>
                        <CountryRow
                            row={row}
                            index={index}
                            remove={removeCountry}
                            update={updateCountry}
                        />
                        {index < riskRules.length - 1 && <hr className="opacity-20"/>}
                    </React.Fragment>
                ))}

                <div className={"flex gap-4 justify-end"}>
                    {mode == "country" && (
                        <div className={"w-1/4"}>
                            <CountrySelectSingle value={selectedCountryISO} setValue={setSelectedCountryISO}/>
                        </div>
                    )}
                    <div className={"w-1/4"}>
                        <IconButton text="Add" icon={<AddIcon/>} onClick={addItem} disabled={!canAdd}/>
                    </div>
                </div>


                <div className="grid grid-cols-2 gap-8">
                    <div className={"flex flex-col gap-4"}>
                        <h2 className="font-semibold text-lg">Exclude Countries</h2>

                        {mode == "risk" && <MultiInputSingleField
                            noSeparators
                            unique
                            initialValues={excludedCountries}
                            type={"country"}
                            onChange={setExcludedCountries}/>
                        }
                        <ToggleButton text={"Exclude User's Country of Residence"} value={excludeUsersCountry}
                                      onChange={setExcludeUsersCountry} initialValue={excludeUsersCountry}/>

                    </div>

                    <div className={"flex flex-col gap-4"}>
                        <h2 className="font-semibold text-lg">Apply To Departments</h2>
                        {profile.default && (
                            <p>{"Departments can't be configured for default profile"}</p>
                        )}

                        {!profile.default && (
                            <>
                                {departments.length != 0 && <div className={"flex flex-col gap-4"}>
                                    {departments.map((department, index) => (
                                        <div key={department.id}>
                                            <div className="grid grid-cols-2 gap-8">
                                                <div className="flex items-center">{department.name}</div>
                                                <ButtonV2 color={"orange"} label={<DeleteIcon/>}
                                                          onClick={() => removeDepartment(index)}/>

                                            </div>
                                        </div>
                                    ))}
                                </div>}
                                <div className="grid grid-cols-2 gap-8">

                                    <DepartmentSelectV2 clientID={client.id} onChange={setSelectedDepartment}/>
                                    <div className="flex flex-col gap-2">
                                        <ButtonV2 color={"blue"} label={<AddIcon/>} onClick={addDepartment}/>
                                        <ErrorMessage text={departmentAddError}/>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <div className={"flex justify-end"}>
                    <div className="w-[200px] flex flex-col gap-2">
                        <IconButton color="green"
                                    icon={submittingUpdate ? <LoadingSpinner width={20} height={20} color="#ffffff"/> :
                                        <SaveIcon/>} text={"Save"} onClick={handleSave} disabled={submittingUpdate}/>
                        <ErrorMessage text={updateError}/>
                        <SuccessMessage text={updateMessage}/>
                    </div>
                </div>

            </div>
        </>
    )


}


export function ErrorMessage({text}: { text: string | undefined }) {
    if (!text) {
        return null
    }

    return (
        <p className={"text-sm text-center text-red"}>
            {text}
        </p>
    )

}

export function SuccessMessage({text}: { text: string | undefined }) {
    if (!text) {
        return null
    }

    return (
        <p className={"text-sm text-center text-green"}>
            {text}
        </p>
    )

}

type HeaderProps = {
    mode: "risk" | "country"
}

const Heading = ({mode}: HeaderProps) => {

    const containerClass = useMemo(() => {
        return clsx(
            "rounded-t-lg bg-desaturated-grey py-3 grid w-full items-center",
            {
                "grid-cols-6": mode === "risk",
                "grid-cols-5": mode === "country",
            }
        )
    }, [mode])


    return <div className={containerClass}>

        {mode == "risk" && (
            <>
                <div className="font-bold border-r-1 text-center relative h-full flex items-center justify-center">
                    Min Risk
                </div>
                <div className="font-bold border-r-1 text-center relative h-full flex items-center justify-center">
                    Max Risk
                </div>
            </>
        )}

        {mode == "country" && (
            <div className="font-bold border-r-1 text-center relative h-full flex items-center justify-center">
                Country
            </div>
        )}
        <div className="font-bold border-r-1 text-center relative h-full flex items-center justify-center">
            Times
        </div>
        <div className="font-bold border-r-1 text-center relative h-full flex items-center justify-center">
            Arrival
        </div>
        <div className="font-bold border-r-1 text-center relative h-full flex items-center justify-center">
            Departure
        </div>

        <div className="font-bold text-center px-6 h-full flex items-center justify-center">Actions</div>

    </div>
}